"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.voiceService = exports.UnifiedVoiceService = exports.VoiceError = exports.VideoQuality = void 0;
const agora_rtc_sdk_ng_1 = __importDefault(require("agora-rtc-sdk-ng"));
const agoraClient_1 = require("./agoraClient");
const NativeVoiceService_1 = require("./NativeVoiceService");
const agoraConfig_1 = require("../config/agoraConfig");
const MeciVoiceService_1 = require("./MeciVoiceService");
const realtimeConfig_1 = require("../config/realtimeConfig");
const AudioService_1 = require("./AudioService");
const isElectron = typeof window !== 'undefined' &&
    window.process &&
    window.process.type === 'renderer';
exports.VideoQuality = {
    '120p': { width: 160, height: 120, frameRate: 15, bitrateMax: 200 },
    '180p': { width: 320, height: 180, frameRate: 15, bitrateMax: 400 },
    '240p': { width: 320, height: 240, frameRate: 15, bitrateMax: 500 },
    '360p': { width: 640, height: 360, frameRate: 15, bitrateMax: 800 },
    '480p': { width: 640, height: 480, frameRate: 15, bitrateMax: 1000 },
    '720p': { width: 1280, height: 720, frameRate: 30, bitrateMax: 2000 },
    '1080p': { width: 1920, height: 1080, frameRate: 30, bitrateMax: 4000 },
};
class VoiceError extends Error {
    constructor(type, message, originalError) {
        super(message);
        this.type = type;
        this.originalError = originalError;
        this.name = 'VoiceError';
    }
}
exports.VoiceError = VoiceError;
class AgoraVoiceService {
    constructor() {
        this.client = (0, agoraClient_1.getAgoraClient)();
        this.listeners = new Map();
        this.channelName = null;
        this.uid = null;
        this.eventsBound = false;
        this.statsTimer = null;
        this.isScreenSharing = false;
        this.isDeafened = false;
        this.remoteVideoTracks = new Map();
        this.remoteAudioTracks = new Map();
    }
    // ── Event emitter helpers ─────────────────────────────────────────
    emit(event, payload) {
        this.listeners.get(event)?.forEach(l => l(payload));
    }
    on(event, listener) {
        if (!this.listeners.has(event))
            this.listeners.set(event, new Set());
        this.listeners.get(event).add(listener);
    }
    off(event, listener) {
        this.listeners.get(event)?.delete(listener);
    }
    removeAllListeners() { this.listeners.clear(); }
    // ── Internal: bind Agora web client events once ───────────────────
    ensureEventBindings() {
        if (this.eventsBound)
            return;
        this.eventsBound = true;
        this.client.on('user-published', async (user, mediaType) => {
            await this.client.subscribe(user, mediaType);
            if (mediaType === 'audio' && user.audioTrack) {
                this.remoteAudioTracks.set(String(user.uid), user.audioTrack);
                if (this.isDeafened) {
                    user.audioTrack.setVolume(0);
                }
                else {
                    user.audioTrack.setVolume(100);
                    user.audioTrack.play();
                }
            }
            if (mediaType === 'video' && user.videoTrack) {
                this.remoteVideoTracks.set(String(user.uid), user.videoTrack);
            }
            this.emit('user-published', {
                user, mediaType,
                audioTrack: user.audioTrack || null,
                videoTrack: user.videoTrack || null,
            });
        });
        this.client.on('user-unpublished', (user, mediaType) => {
            if (mediaType === 'audio') {
                this.remoteAudioTracks.delete(String(user.uid));
            }
            if (mediaType === 'video') {
                this.remoteVideoTracks.delete(String(user.uid));
            }
            this.emit('user-unpublished', { uid: user.uid, mediaType });
        });
        this.client.on('user-joined', user => {
            AudioService_1.audioService.playJoinVoice();
            this.emit('user-joined', { user });
        });
        this.client.on('user-left', user => {
            this.remoteAudioTracks.delete(String(user.uid));
            this.remoteVideoTracks.delete(String(user.uid));
            AudioService_1.audioService.playLeaveVoice();
            this.emit('user-left', { uid: user.uid });
        });
        this.client.on('connection-state-change', curState => this.emit('connection-state-change', { curState }));
        this.client.enableAudioVolumeIndicator();
        this.client.on('volume-indicator', volumes => volumes.forEach(i => this.emit('volume-indicator', { uid: i.uid, level: i.level })));
    }
    // ── Join ──────────────────────────────────────────────────────────
    async joinChannel(channelName, uid) {
        this.channelName = channelName;
        this.uid = uid;
        // Web tarayıcısında insecure context (HTTP IP adresi) kontrolü
        if (!isElectron && typeof window !== 'undefined' && window.isSecureContext === false) {
            console.warn('[VoiceService] Insecure context detected on web:', window.location.href);
            throw new Error('Web tarayıcılarında sesli sohbet (WebRTC) sadece HTTPS veya http://localhost üzerinden çalışır. IP adresi (örn. http://192.168.2.104:3000) üzerinden mikrofona erişim tarayıcı güvenlik politikası nedeniyle engellenmektedir. Lütfen tarayıcıda http://localhost:3000 adresini kullanın.');
        }
        this.ensureEventBindings();
        try {
            const { token, appId: serverAppId } = await this.fetchToken(channelName, uid);
            const activeAppId = serverAppId || agoraConfig_1.AGORA_APP_ID;
            if (isElectron) {
                await NativeVoiceService_1.nativeVoiceService.initialize(activeAppId, (n, ...a) => this.emit(n, ...a));
                await NativeVoiceService_1.nativeVoiceService.join(channelName, token || '', Number(uid));
                // Camera starts OFF in native
                await NativeVoiceService_1.nativeVoiceService.setVideoEnabled(false);
            }
            else {
                try {
                    await this.client.join(activeAppId, channelName, token, Number(uid));
                }
                catch (joinErr) {
                    if (token && (joinErr?.message?.includes('CAN_NOT_GET_GATEWAY_SERVER') || joinErr?.code === 'CAN_NOT_GET_GATEWAY_SERVER' || joinErr?.message?.includes('invalid vendor key'))) {
                        console.warn('[VoiceService] Token join failed, retrying with null token (testing mode without certificate)...');
                        await this.client.join(activeAppId, channelName, null, Number(uid));
                    }
                    else {
                        throw joinErr;
                    }
                }
            }
            // Only create audio track; camera starts closed
            const { localAudioTrack } = await (0, agoraClient_1.createLocalTracks)(undefined, { video: false });
            if (!isElectron && localAudioTrack)
                await this.client.publish(localAudioTrack);
            this.emit('local-tracks', { audioTrack: localAudioTrack, videoTrack: null });
            this.startStatsPolling();
        }
        catch (e) {
            console.error('[VoiceService] joinChannel error:', e);
            if (e?.message?.includes('WEB_SECURITY_RESTRICT') ||
                e?.message?.includes('enumerateDevices') ||
                e?.name === 'NOT_SUPPORTED') {
                throw new Error('Tarayıcınız WebRTC/Mikrofon erişimini kısıtladı. Sesli kanalları kullanabilmek için tarayıcıda http://localhost:3000 veya HTTPS adresini açmalısınız.');
            }
            if (e?.message?.includes('CAN_NOT_GET_GATEWAY_SERVER') || e?.message?.includes('invalid vendor key') || e?.code === 'CAN_NOT_GET_GATEWAY_SERVER') {
                throw new Error('Agora App ID geçersiz veya Agora Console üzerinde bulunamadı. Lütfen agoraConfig.ts ve phpapi/config.php dosyalarındaki AGORA_APP_ID değerini Agora Console (console.agora.io) üzerinden aldığınız geçerli App ID ile güncelleyin.');
            }
            throw e;
        }
    }
    // ── Leave ─────────────────────────────────────────────────────────
    async leaveChannel() {
        if (isElectron)
            await NativeVoiceService_1.nativeVoiceService.leave();
        try {
            await this.client.leave();
        }
        catch (_) { }
        (0, agoraClient_1.cleanupLocalTracks)();
        this.stopStatsPolling();
    }
    // ── Camera toggle ─────────────────────────────────────────────────
    async toggleVideo(enabled) {
        // Electron: native SDK handles camera directly
        if (isElectron) {
            await NativeVoiceService_1.nativeVoiceService.setVideoEnabled(enabled);
            return;
        }
        // Web SDK
        try {
            const { localVideoTrack } = (0, agoraClient_1.getLocalTracks)();
            if (enabled) {
                if (localVideoTrack) {
                    if (!localVideoTrack.enabled)
                        await localVideoTrack.setEnabled(true);
                }
                else {
                    const cam = await (0, agoraClient_1.createCameraTrack)();
                    if (!cam) {
                        console.warn('[VoiceService] Camera not available');
                        return;
                    }
                    await this.client.publish(cam);
                    this.emit('local-tracks', {
                        audioTrack: (0, agoraClient_1.getLocalTracks)().localAudioTrack,
                        videoTrack: cam,
                    });
                }
            }
            else {
                if (localVideoTrack) {
                    try {
                        await this.client.unpublish(localVideoTrack);
                    }
                    catch (_) { }
                    localVideoTrack.stop();
                    localVideoTrack.close();
                    (0, agoraClient_1.clearLocalVideoTrack)(); // null the module-level ref so re-enable creates a fresh track
                    this.emit('local-tracks', {
                        audioTrack: (0, agoraClient_1.getLocalTracks)().localAudioTrack,
                        videoTrack: null,
                    });
                }
            }
        }
        catch (err) {
            console.warn('[VoiceService] toggleVideo error:', err);
        }
    }
    // ── Mute & Deafen toggle ──────────────────────────────────────────
    async toggleMute(muted) {
        if (isElectron) {
            await NativeVoiceService_1.nativeVoiceService.setMute(muted);
            return;
        }
        const { localAudioTrack } = (0, agoraClient_1.getLocalTracks)();
        if (localAudioTrack) {
            await localAudioTrack.setMuted(muted);
            await localAudioTrack.setEnabled(!muted);
        }
    }
    async toggleDeafen(deafened) {
        this.isDeafened = deafened;
        if (isElectron) {
            await NativeVoiceService_1.nativeVoiceService.setDeafen(deafened);
            return;
        }
        // Web: Set volume to 0 or 100 on all remote audio tracks
        this.client.remoteUsers.forEach((user) => {
            if (user.audioTrack) {
                user.audioTrack.setVolume(deafened ? 0 : 100);
            }
        });
        // Also mute local mic when deafened
        await this.toggleMute(deafened);
    }
    // ── Screen share ──────────────────────────────────────────────────
    async toggleScreenShare(enabled, sourceId) {
        if (isElectron) {
            if (enabled && sourceId) {
                await NativeVoiceService_1.nativeVoiceService.startScreenShare(sourceId);
            }
            else {
                // Stop share — just disable, no need to leave/rejoin
                NativeVoiceService_1.nativeVoiceService.stopScreenShare();
            }
            this.isScreenSharing = enabled;
            this.emit('screen-share-changed', enabled);
            return;
        }
        // Web
        if (enabled) {
            const track = await (0, agoraClient_1.createScreenTrack)();
            if (track)
                await this.client.publish(track);
        }
        else {
            const screen = (0, agoraClient_1.getScreenTrack)();
            if (screen) {
                try {
                    await this.client.unpublish(screen);
                }
                catch (_) { }
                screen.close();
            }
        }
        this.isScreenSharing = enabled;
    }
    // ── Device management ─────────────────────────────────────────────
    async listDevices() {
        const devices = await agora_rtc_sdk_ng_1.default.getDevices();
        return {
            microphones: devices.filter(d => d.kind === 'audioinput'),
            cameras: devices.filter(d => d.kind === 'videoinput'),
            speakers: devices.filter(d => d.kind === 'audiooutput'),
        };
    }
    async setMicrophone(id) {
        const { localAudioTrack } = (0, agoraClient_1.getLocalTracks)();
        if (localAudioTrack)
            await localAudioTrack.setDevice(id);
    }
    async setCamera(id) {
        const { localVideoTrack } = (0, agoraClient_1.getLocalTracks)();
        if (localVideoTrack)
            await localVideoTrack.setDevice(id);
    }
    // ── Video Track & DOM Binding ─────────────────────────────────────
    getVideoTrack(uidStr) {
        if (this.uid && String(this.uid) === uidStr) {
            return (0, agoraClient_1.getLocalTracks)().localVideoTrack;
        }
        return this.remoteVideoTracks.get(uidStr);
    }
    attachVideo(uidStr, element) {
        try {
            const track = this.getVideoTrack(uidStr);
            if (track && element) {
                track.play(element);
            }
        }
        catch (e) {
            console.warn('[AgoraVoiceService] attachVideo error:', e);
        }
    }
    setRemoteVolume(uidStr, volume) {
        try {
            const track = this.remoteAudioTracks.get(uidStr);
            if (track && typeof track.setVolume === 'function') {
                track.setVolume(Math.round(Math.max(0, Math.min(200, volume))));
            }
        }
        catch (e) {
            console.warn('[AgoraVoiceService] setRemoteVolume error:', e);
        }
    }
    setVideoQuality(qualityKey) {
        try {
            const { localVideoTrack } = (0, agoraClient_1.getLocalTracks)();
            if (localVideoTrack && typeof localVideoTrack.setEncoderConfiguration === 'function') {
                const config = exports.VideoQuality[qualityKey] || exports.VideoQuality['720p'];
                localVideoTrack.setEncoderConfiguration(config);
            }
        }
        catch (e) {
            console.warn('[AgoraVoiceService] setVideoQuality error:', e);
        }
    }
    // ── Helpers ───────────────────────────────────────────────────────
    async fetchToken(c, u) {
        try {
            const r = await fetch(`${agoraConfig_1.AGORA_TOKEN_ENDPOINT}?channelName=${c}&uid=${u}`);
            if (!r.ok)
                return { token: null, appId: agoraConfig_1.AGORA_APP_ID };
            const data = await r.json();
            return { token: data.token || null, appId: data.appId || agoraConfig_1.AGORA_APP_ID };
        }
        catch (err) {
            console.warn('[VoiceService] Could not fetch Agora token from endpoint, falling back to default App ID:', err);
            return { token: null, appId: agoraConfig_1.AGORA_APP_ID };
        }
    }
    startStatsPolling() {
        this.statsTimer = setInterval(() => {
            const s = this.client.getRTCStats();
            if (s)
                this.emit('stats', { rtt: s.RTT });
        }, 3000);
    }
    stopStatsPolling() {
        if (this.statsTimer) {
            clearInterval(this.statsTimer);
            this.statsTimer = null;
        }
    }
}
class UnifiedVoiceService {
    constructor() {
        this.agoraService = new AgoraVoiceService();
        this.meciService = MeciVoiceService_1.meciVoiceService;
    }
    getActiveService() {
        return (0, realtimeConfig_1.getVoiceEngine)() === 'agora' ? this.agoraService : this.meciService;
    }
    on(event, listener) {
        this.agoraService.on(event, listener);
        this.meciService.on(event, listener);
    }
    off(event, listener) {
        this.agoraService.off(event, listener);
        this.meciService.off(event, listener);
    }
    removeAllListeners() {
        this.agoraService.removeAllListeners();
        this.meciService.removeAllListeners();
    }
    async joinChannel(channelName, uid, options) {
        const engine = (0, realtimeConfig_1.getVoiceEngine)();
        console.log(`[UnifiedVoiceService] Joining channel ${channelName} with engine: ${engine}`);
        if (engine === 'agora') {
            return this.agoraService.joinChannel(channelName, uid);
        }
        else {
            return this.meciService.joinChannel(channelName, uid, options);
        }
    }
    async leaveChannel() {
        await Promise.allSettled([
            this.agoraService.leaveChannel(),
            this.meciService.leaveChannel(),
        ]);
    }
    async toggleVideo(enabled) {
        return this.getActiveService().toggleVideo(enabled);
    }
    async toggleMute(muted) {
        return this.getActiveService().toggleMute(muted);
    }
    async toggleDeafen(deafened) {
        return this.getActiveService().toggleDeafen(deafened);
    }
    async toggleScreenShare(enabled, sourceId, withAudio = true) {
        return this.getActiveService().toggleScreenShare(enabled, sourceId, withAudio);
    }
    getVideoTrack(uidStr) {
        const s = this.getActiveService();
        return s.getVideoTrack ? s.getVideoTrack(uidStr) : undefined;
    }
    attachVideo(uidStr, element) {
        const s = this.getActiveService();
        if (s.attachVideo) {
            s.attachVideo(uidStr, element);
        }
    }
    getLiveSnapshot() {
        return this.meciService.getLiveSnapshot();
    }
    async listDevices() {
        if (typeof navigator !== 'undefined' && navigator.mediaDevices?.enumerateDevices) {
            try {
                const devices = await navigator.mediaDevices.enumerateDevices();
                return {
                    microphones: devices.filter(d => d.kind === 'audioinput'),
                    speakers: devices.filter(d => d.kind === 'audiooutput'),
                    cameras: devices.filter(d => d.kind === 'videoinput'),
                };
            }
            catch (e) { }
        }
        return this.agoraService.listDevices();
    }
    async setMicrophone(id) {
        const s = this.getActiveService();
        if (s && typeof s.setMicrophone === 'function') {
            await s.setMicrophone(id);
        }
        return this.agoraService.setMicrophone(id);
    }
    async setCamera(id) {
        const s = this.getActiveService();
        if (s && typeof s.setCamera === 'function') {
            await s.setCamera(id);
        }
        return this.agoraService.setCamera(id);
    }
    async setOutputDevice(id) {
        const s = this.getActiveService();
        if (s && typeof s.setOutputDevice === 'function') {
            await s.setOutputDevice(id);
        }
    }
    setRemoteVolume(uidStr, volume) {
        const s = this.getActiveService();
        if (s.setRemoteVolume) {
            s.setRemoteVolume(uidStr, volume);
        }
    }
    setRemoteStreamVolume(uidStr, volume) {
        const s = this.getActiveService();
        if (s && typeof s.setRemoteStreamVolume === 'function') {
            s.setRemoteStreamVolume(uidStr, volume);
        }
    }
    setRemoteStreamMuted(uidStr, muted) {
        const s = this.getActiveService();
        if (s && typeof s.setRemoteStreamMuted === 'function') {
            s.setRemoteStreamMuted(uidStr, muted);
        }
    }
    async setNoiseSuppression(enabled, engine) {
        const s = this.getActiveService();
        if (s && typeof s.setNoiseSuppression === 'function') {
            return s.setNoiseSuppression(enabled, engine);
        }
    }
    setVideoQuality(qualityKey) {
        const s = this.getActiveService();
        if (s.setVideoQuality) {
            s.setVideoQuality(qualityKey);
        }
    }
    sendAdminModeration(targetSocketId, targetUserId, action, value) {
        const s = this.getActiveService();
        if (s && typeof s.sendAdminModeration === 'function') {
            return s.sendAdminModeration(targetSocketId, targetUserId, action, value);
        }
    }
}
exports.UnifiedVoiceService = UnifiedVoiceService;
exports.voiceService = new UnifiedVoiceService();
